import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { TrendingUp, Users, AlertTriangle, Percent, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { ConsolidacaoResponse } from '../../api/hooks/useRelatorios';
import { DIAS_UTEIS } from './useProfessorGradeSemanal';

const LABEL_DIA: Record<string, string> = {
  SEGUNDA: 'Seg',
  TERCA: 'Ter',
  QUARTA: 'Qua',
  QUINTA: 'Qui',
  SEXTA: 'Sex',
  SABADO: 'Sáb',
};

const PIE_COLORS = ['#10b981', '#f87171', '#94a3b8'];

interface TurmaFreq {
  turmaId?: string;
  nome: string;
  label?: string;
  turno?: string;
  media: number;
  emRisco: number;
  alunos: number;
}

interface Props {
  resumo?: ConsolidacaoResponse['resumo'];
  turmasFrequencia: TurmaFreq[];
  countsSemana: Record<string, number>;
  isLoading?: boolean;
}

function freqBarColor(pct: number): string {
  if (pct >= 80) return 'linear-gradient(90deg, #34d399, #059669)';
  if (pct >= 60) return 'linear-gradient(90deg, #fbbf24, #d97706)';
  return 'linear-gradient(90deg, #fca5a5, #dc2626)';
}

function TurmaRankList({ turmas }: { turmas: TurmaFreq[] }) {
  const sorted = [...turmas].sort((a, b) => b.media - a.media);

  return (
    <div className="dash-desk-turma-rank" role="list">
      {sorted.map((t) => {
        const pct = Math.round(t.media);
        const label =
          t.label ||
          (t.turno && !t.nome.toLowerCase().includes(t.turno.toLowerCase())
            ? `${t.nome} · ${t.turno}`
            : t.nome);
        return (
          <div
            key={t.turmaId || label}
            className="dash-desk-turma-rank-row"
            role="listitem"
            title={`${label} — ${pct}% · ${t.alunos} alunos${t.emRisco ? ` · ${t.emRisco} em atenção` : ''}`}
          >
            <span className="dash-desk-turma-rank-name">{label}</span>
            <div className="dash-desk-turma-rank-track" aria-hidden>
              <div
                className="dash-desk-turma-rank-fill"
                style={{ width: `${Math.min(100, pct)}%`, background: freqBarColor(pct) }}
              />
            </div>
            <span className={`dash-desk-turma-rank-pct${pct < 60 ? ' warn' : ''}`}>{pct}%</span>
            {t.emRisco > 0 && (
              <span className="dash-desk-turma-rank-risk" title={`${t.emRisco} em atenção`}>
                {t.emRisco}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

const tooltipStyle = {
  fontSize: 11,
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  boxShadow: '0 4px 12px rgba(15,23,42,0.08)',
};

export function DesktopAnalyticsCharts({
  resumo,
  turmasFrequencia,
  countsSemana,
  isLoading,
}: Props) {
  const navigate = useNavigate();
  const mediaPct = resumo?.mediaFrequencia ?? 0;
  const totalReg = resumo?.totalPresencasDias ?? 0;
  const ausencias = resumo?.totalAusencias ?? 0;
  const presencas = Math.max(0, totalReg - ausencias);
  const hasRegistros = totalReg > 0;

  const presencaPie = hasRegistros
    ? [
        { name: 'Presenças', value: presencas },
        { name: 'Ausências', value: ausencias },
      ]
    : [{ name: 'Sem dados', value: 1 }];

  const cargaSemana = DIAS_UTEIS.map((d) => ({
    dia: LABEL_DIA[d],
    aulas: countsSemana[d] ?? 0,
  }));

  const maxCarga = Math.max(1, ...cargaSemana.map((c) => c.aulas));

  const turmasValidas =
    resumo?.totalTurmas && turmasFrequencia.length === 0 ? [] : turmasFrequencia;

  if (isLoading) {
    return (
      <div className="dash-desk-analytics dash-desk-analytics--loading" aria-busy="true">
        <div className="dash-desk-analytics-skeleton" />
        <div className="dash-desk-analytics-skeleton" />
        <div className="dash-desk-analytics-skeleton" />
      </div>
    );
  }

  return (
    <section className="dash-desk-analytics" aria-label="Indicadores pedagógicos">
      {/* Frequência geral — donut grande + KPIs */}
      <div className="dash-desk-analytics-card dash-desk-analytics-card--highlight dash-desk-analytics-card--freq">
        <div className="dash-desk-analytics-card-head">
          <Percent style={{ color: '#2563eb' }} />
          <span>Frequência (minhas turmas)</span>
        </div>
        <div className="dash-desk-freq-layout">
          <div className="dash-desk-freq-kpis">
            <div className="dash-desk-analytics-big">
              {resumo?.mediaFrequencia?.toFixed(1) ?? '—'}%
            </div>
            <div className="dash-desk-analytics-meta dash-desk-analytics-meta--col">
              <span>
                <Users size={11} /> {resumo?.totalAlunos ?? 0} alunos
              </span>
              <span>
                <AlertTriangle size={11} /> {resumo?.alunosEmRisco ?? 0} em atenção
              </span>
            </div>
            <div className="dash-desk-pie-legend">
              {hasRegistros ? (
                <>
                  <span title="Registros de presença no período (não é contagem de alunos)">
                    <i style={{ background: PIE_COLORS[0] }} /> {presencas} reg. pres.
                  </span>
                  <span title="Registros de ausência no período (não é contagem de alunos)">
                    <i style={{ background: PIE_COLORS[1] }} /> {ausencias} reg. aus.
                  </span>
                </>
              ) : (
                <span className="muted">Sem registros no período</span>
              )}
            </div>
          </div>
          <div className="dash-desk-donut-wrap">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
              <PieChart>
                <Pie
                  data={presencaPie}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  innerRadius="58%"
                  outerRadius="88%"
                  paddingAngle={hasRegistros ? 3 : 0}
                  stroke="none"
                  startAngle={90}
                  endAngle={-270}
                >
                  {presencaPie.map((_, i) => (
                    <Cell key={i} fill={hasRegistros ? PIE_COLORS[i] : PIE_COLORS[2]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v: any, name: any) => [`${v}`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="dash-desk-donut-center">
              <span className="dash-desk-donut-center-val">
                {hasRegistros ? `${mediaPct.toFixed(0)}%` : '—'}
              </span>
              <span className="dash-desk-donut-center-lbl">média</span>
            </div>
          </div>
        </div>
      </div>

      {/* Carga semanal — área + linha */}
      <div className="dash-desk-analytics-card dash-desk-analytics-card--chart">
        <div className="dash-desk-analytics-card-head">
          <TrendingUp style={{ color: '#7c3aed' }} />
          <span>Carga semanal (grade)</span>
          <span className="dash-desk-analytics-head-badge">
            {cargaSemana.reduce((s, c) => s + c.aulas, 0)} aulas/semana
          </span>
        </div>
        <div className="dash-desk-analytics-chart dash-desk-analytics-chart--tall">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
            <AreaChart data={cargaSemana} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="cargaAreaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#818cf8" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#818cf8" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="dia"
                tick={{ fontSize: 10, fill: '#64748b' }}
                axisLine={false}
                tickLine={false}
                interval={0}
              />
              <YAxis
                allowDecimals={false}
                domain={[0, maxCarga]}
                tick={{ fontSize: 9, fill: '#94a3b8' }}
                width={20}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v: any) => [`${v} aula(s)`, 'Carga']}
                labelFormatter={(l) => `${l}`}
              />
              <Area
                type="monotone"
                dataKey="aulas"
                stroke="#6366f1"
                strokeWidth={2.5}
                fill="url(#cargaAreaGrad)"
                dot={{ r: 3, fill: '#6366f1', stroke: '#fff', strokeWidth: 2 }}
                activeDot={{ r: 5, fill: '#4f46e5', stroke: '#fff', strokeWidth: 2 }}
                name="Aulas"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Turmas — ranking com barras (escala com N turmas) */}
      <div className="dash-desk-analytics-card dash-desk-analytics-card--chart dash-desk-analytics-card--turmas">
        <div className="dash-desk-analytics-card-head">
          <BarChart3 style={{ color: '#059669' }} />
          <span>Por turma</span>
          {turmasValidas.length > 0 && (
            <span className="dash-desk-analytics-head-badge">{turmasValidas.length} turmas</span>
          )}
        </div>
        {turmasValidas.length === 0 ? (
          <p className="dash-desk-analytics-empty">Sem dados — veja Relatórios.</p>
        ) : (
          <TurmaRankList turmas={turmasValidas} />
        )}
        <button
          type="button"
          className="dash-desk-analytics-link"
          onClick={() => navigate('/professor/relatorios')}
        >
          Relatórios →
        </button>
      </div>
    </section>
  );
}
